import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from '../../../utils/baseQuery';

const BE_URL_ROUTES = {
  REGISTER: '/auth/register',
  LOGIN: '/auth/login',
  VERIFY_EMAIL: '/auth/send-code',
  VERIFY_CODE: '/auth/verify-code',
  LOGOUT: '/auth/logout',
  RESET_PWD: '/auth/reset-password',
  GET_PLAN: '/auth/getPlans',
};

type registerUserParams = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  organizationName: string;
  CRNumber: string;
  businessIndustry: string;
  companySize: string;
  captchaToken: any;
  planQuantity: string;
  planId: string;
  packagesId: string;
};

type verifyCodeParamType = {
  email: string;
  code: string;
  isResetPwd: boolean;
  password: string;
  firstName: string;
  lastName: string;
};

type verifyCodeBodyType = {
  email: string;
  code: string;
  password?: string;
  firstName?: string;
  lastName?: string;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  endpoints: (builder) => ({
    // Register User
    registerUser: builder.mutation({
      query: ({
        firstName,
        lastName,
        email,
        password,
        organizationName,
        CRNumber,
        businessIndustry,
        companySize,
        captchaToken,
        planQuantity,
        planId,
        packagesId,
      }: registerUserParams) => ({
        url: BE_URL_ROUTES.REGISTER,
        method: 'POST',
        body: {
          firstName,
          lastName,
          email,
          password,
          tenantName: organizationName,
          regNumber: CRNumber,
          businessIndustry,
          companySize,
          captchaToken,
          planQuantity,
          planId,
          packagesId,
        },
      }),
    }),
    // Login User
    loginUser: builder.mutation({
      query: ({ email, password, captchaToken }) => ({
        url: BE_URL_ROUTES.LOGIN,
        method: 'POST',
        body: { email, password, captchaToken },
      }),
    }),
    // Verify Email (For New User) | Reset Password => (Email Verification)
    verifyEmail: builder.mutation({
      query: ({ email, isResetPwd = false }) => ({
        url: BE_URL_ROUTES.VERIFY_EMAIL,
        method: 'POST',
        body: { email, type: isResetPwd ? 'RESET PASSWORD' : 'EMAIL VERIFICATION' },
      }),
    }),
    // Verify Code (Email Verification) | Reset Password (Reset-Pwd)
    verifyCode: builder.mutation({
      query: ({
        email,
        code,
        isResetPwd = false,
        password,
        firstName,
        lastName,
      }: verifyCodeParamType) => {
        const body: verifyCodeBodyType = { email, code, firstName, lastName };
        if (isResetPwd && password) {
          delete body.firstName;
          delete body.lastName;
          body.password = password;
        }
        return {
          url: BE_URL_ROUTES.VERIFY_CODE,
          method: 'POST',
          body,
        };
      },
    }),
    // Reset Pwd
    resetPwd: builder.mutation({
      query: ({ email, code, password }: verifyCodeParamType) => {
        const body: verifyCodeBodyType = { email, code, password };
        return {
          url: BE_URL_ROUTES.RESET_PWD,
          method: 'POST',
          body,
        };
      },
    }),
    // Logout User
    logoutUser: builder.mutation({
      query: ({ email }) => ({
        url: BE_URL_ROUTES.LOGOUT,
        method: 'POST',
        body: { email },
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ user_id, currentPassword, newPassword }) => ({
        url: 'user/resetPassword', // Use the correct endpoint
        method: 'POST',
        body: {
          user_id,
          password: currentPassword,
          newpassword: newPassword,
        },
      }),
    }),

    autoLoginUser: builder.mutation({
      query: ({ email, imPersonateToken, captchaToken }) => ({
        url: BE_URL_ROUTES.LOGIN,
        method: 'POST',
        body: { email, imPersonateToken, captchaToken },
      }),
    }),

    getPlan: builder.mutation({
      query: () => ({
        url: BE_URL_ROUTES.GET_PLAN,
        method: 'POST',
        body: {},
      }),
    }),
  }),
});

export const {
  useRegisterUserMutation,
  useLoginUserMutation,
  useVerifyEmailMutation,
  useVerifyCodeMutation,
  useLogoutUserMutation,
  useResetPwdMutation,
  useResetPasswordMutation,
  useAutoLoginUserMutation,
  useGetPlanMutation,
} = authApi;
